import React, { useEffect, useRef, useState } from 'react';

import vestaFrames   from '../assets/clients/VestaFrames.png';
import setia         from '../assets/clients/Setia.png';
import livguard      from '../assets/clients/Livguard.png';
import diamond       from '../assets/clients/DiamondModular.png';
import energo        from '../assets/clients/Energo.png';
import geEmm         from '../assets/clients/GeEmmIndustries.png';

const LOGOS = [
  { src: vestaFrames, alt: 'Vesta Frames' },
  { src: setia,       alt: 'Setia uPVC Pipes' },
  { src: livguard,    alt: 'Livguard' },
  { src: diamond,     alt: 'Diamond Modular' },
  { src: energo,      alt: 'Energo' },
  { src: geEmm,       alt: 'GEE EMM Industries' },
];

const LOGO_HEIGHT = 72;
const CARD_W = 190;
const CARD_H = 84;

const ClientLogos: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const wrapRef  = useRef<HTMLDivElement>(null);
  const [needsScroll, setNeedsScroll] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 640);
      if (trackRef.current && wrapRef.current) {
        setNeedsScroll(trackRef.current.scrollWidth > wrapRef.current.clientWidth + 1);
      }
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Determine if we need to shrink cards to fit in one line
  // If not mobile, and not needsScroll, but window is < 1350px, shrink
  const [shrink, setShrink] = useState(false);
  useEffect(() => {
    const checkShrink = () => {
      if (window.innerWidth < 1350 && !isMobile && !needsScroll) {
        setShrink(true);
      } else {
        setShrink(false);
      }
    };
    checkShrink();
    window.addEventListener('resize', checkShrink);
    return () => window.removeEventListener('resize', checkShrink);
  }, [isMobile, needsScroll]);

  const displayLogos = needsScroll ? [...LOGOS, ...LOGOS] : LOGOS;

  return (
    <section style={{
      background: '#f8fafc',
      padding: '20px 0 22px',
      overflow: 'hidden',
      position: 'relative',
      borderTop: '1px solid #e8edf2',
      borderBottom: '1px solid #e8edf2',
    }}>
      <style>{`
        @keyframes marqueeScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .cl-track-scroll {
          display: flex;
          width: max-content;
          animation: marqueeScroll 32s linear infinite;
          will-change: transform;
        }
        .cl-track-scroll:hover { animation-play-state: paused; }
        .cl-track-static {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 28px;
          flex-wrap: nowrap;
          padding: 0 32px;
        }
        .cl-track-static.shrink {
          gap: 12px;
        }
      `}</style>

      {/* Label */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 20,
        maxWidth: 800, margin: '0 auto 18px', padding: '0 24px',
      }}>
        <div style={{ flex: 1, height: 1, background: '#d1dae3' }} />
        <p style={{
          fontSize: isMobile ? 10 : 14, fontWeight: 700, letterSpacing: isMobile ? '0.08em' : '0.14em',
          color: '#4a6080', textTransform: 'uppercase', margin: 0,
          textAlign: 'center', whiteSpace: isMobile ? 'normal' : 'nowrap',
        }}>
          Trusted By Leading Businesses Across India
        </p>
        <div style={{ flex: 1, height: 1, background: '#d1dae3' }} />
      </div>

      {/* Mobile: slow horizontal marquee */}
      {isMobile ? (
        <div style={{ position: 'relative', overflow: 'hidden', padding: '6px 0 4px' }}>
          <div style={{ display: 'flex', width: 'max-content', animation: 'marqueeScroll 18s linear infinite' }}>
            {[...LOGOS, ...LOGOS].map((logo, i) => (
              <div key={i} style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 130, height: 64, padding: '0 12px', margin: '0 10px',
                background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(0,0,0,0.06)', flexShrink: 0,
              }}>
                <img
                  src={logo.src}
                  alt={logo.alt}
                  style={{ height: 46, width: 'auto', maxWidth: 110, objectFit: 'contain', display: 'block' }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
      /* Desktop: marquee strip */
      <div ref={wrapRef} style={{ position: 'relative', padding: '6px 32px 4px' }}>
        {needsScroll && (
          <>
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to right, #f8fafc 50%, transparent)', zIndex: 2, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: 'linear-gradient(to left, #f8fafc 50%, transparent)', zIndex: 2, pointerEvents: 'none' }} />
          </>
        )}

        <div
          ref={trackRef}
          className={
            needsScroll
              ? 'cl-track-scroll'
              : shrink
                ? 'cl-track-static shrink'
                : 'cl-track-static'
          }
        >
          {displayLogos.map((logo, i) => {
            const isHovered = hoveredIdx === i;
            // Shrink card width/gap if shrink is true
            const cardW = shrink ? 140 : CARD_W;
            const cardH = shrink ? 64 : CARD_H;
            const logoH = shrink ? 48 : LOGO_HEIGHT;
            return (
              <div
                key={i}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: cardW,
                  height: cardH,
                  padding: shrink ? '0 8px' : '0 16px',
                  margin: needsScroll ? '0 18px' : '0',
                  background: '#ffffff',
                  borderRadius: 14,
                  border: '1px solid #e2e8f0',
                  boxShadow: isHovered
                    ? '0 8px 28px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)'
                    : '0 2px 10px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
                  flexShrink: 0,
                  transition: 'box-shadow 0.30s cubic-bezier(0.34,1.56,0.64,1), transform 0.30s cubic-bezier(0.34,1.56,0.64,1)',
                  transform: isHovered ? 'translateY(-8px) scale(1.04)' : 'translateY(0px) scale(1)',
                  cursor: 'default',
                }}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  style={{
                    height: logoH,
                    width: 'auto',
                    maxWidth: shrink ? 120 : 200,
                    objectFit: 'contain',
                    display: 'block',
                    filter: 'grayscale(0%) opacity(1)',
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      )}
    </section>
  );
};

export default ClientLogos;
